import { useState, useEffect } from 'react';
import {
  fetchOAuthClients,
  OAuthClientRow,
  OAuthClientsPagination,
} from 'admin/actions/oauth-client';
import { toQueryParams } from 'admin/utils';

export type OAuthClientsQuery = {
  page?: number;
  per_page?: number;
  organization_code?: string | null;
};

export type OAuthClientsError = { message?: string };

type UseOAuthClientsReturn = [
  boolean,
  OAuthClientRow[],
  OAuthClientsPagination,
  number,
  OAuthClientsError,
  (query?: OAuthClientsQuery) => Promise<void>,
];

const removeEmptyValues = (query: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  Object.keys(query).forEach((key) => {
    const value = query[key];
    if (value !== '' && value !== null && value !== undefined) {
      result[key] = value;
    }
  });
  return result;
};

const useOAuthClients = (initQuery: OAuthClientsQuery = {}): UseOAuthClientsReturn => {
  const [oauthClients, setOAuthClients] = useState<OAuthClientRow[]>([]);
  const [pagination, setPagination] = useState<OAuthClientsPagination>({ current: 1, max_page: 1 });
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<OAuthClientsError>({});

  const getOAuthClients = async (query: OAuthClientsQuery = {}): Promise<void> => {
    setIsLoading(true);
    setError({});

    const removed = removeEmptyValues(query);

    await fetchOAuthClients(toQueryParams(removed))
      .then((data) => {
        setOAuthClients(data.rows);
        setPagination(data.pagination);
        setCount(data.count);
      })
      .catch((err: OAuthClientsError) => {
        setError(err);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getOAuthClients(initQuery);
  }, []);

  return [isLoading, oauthClients, pagination, count, error, getOAuthClients];
};

export default useOAuthClients;
